import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData?: { username?: string; full_name?: string }) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 設置認證狀態監聽器
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // 獲取初始會話
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, userData?: { username?: string; full_name?: string }) => {
    try {
      console.log('開始註冊流程:', { email, userData });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });

      console.log('Supabase 註冊結果:', { data, error });

      if (error) {
        console.error('Supabase 註冊錯誤:', error);
        throw error;
      }

      if (data.user) {
        console.log('註冊成功，用戶 ID:', data.user.id);
        
        toast({
          title: "註冊成功",
          description: data.session ? "歡迎加入！" : "請檢查您的郵箱並點擊確認鏈接完成註冊",
        });
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('註冊過程錯誤:', error);
      
      let errorMessage = "註冊過程中發生錯誤";
      if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "註冊失敗",
        description: errorMessage,
        variant: "destructive",
      });
      return { data: null, error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      toast({
        title: "登入成功",
        description: "歡迎回來！",
      });

      return { data, error: null };
    } catch (error: any) {
      toast({
        title: "登入失敗",
        description: error.message,
        variant: "destructive",
      });
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      toast({
        title: "已登出",
        description: "您已成功登出",
      });
    } catch (error: any) {
      toast({
        title: "登出失敗",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};